import { Box, Toolbar, Typography } from "@mui/material";
import { useMemo, useCallback, useEffect, useState } from 'react';
import DeleteConfirmationModal from "../../../../../common/DeleteConfirmationModal";
import GallerySectionEditor from "./GallerySectionEditor";
import { usePropertyPicturesData } from "../../../../../common/hooks/usePropertyPicturesData";
import { withQueryData } from "../../../../../common/withQueryData";
import { SaveButton } from "../../../../../common/Buttons";
import { useFormStatus } from "../../../../../common/inputs/useFormStatus";
import { useAlertNotification } from "../../../../../common/hooks/useAlertNotification";
import { useEditableArray } from "../../../../../common/hooks/useEditableArray";
import { useFormValidator } from "../../../../../common/inputs/useFormValidator";
import { PlusIcon } from "../../../../../common/icons/Icons";
import { ALBUM_ID } from "../../../../../common/enums";

const pictureUrlsToPictures = (pictureUrls) =>
  (pictureUrls || []).map((url) => {
    const id = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));
    return { id, url };
  });

const slugify = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'section';
};

const PropertyPicturesEditor = ({ propertyId }) => {
  const { setAlert } = useAlertNotification();

  const {
    picturesQueryResults,
    createPicturesAlbum,
    uploadPictures,
    updatePicturesAlbum,
    deletePicturesAlbum,
    deletePictures,
  } = usePropertyPicturesData(propertyId);

  const onSubmitSection = useCallback(async (section, index, existingAlbums) => {
    const isNew = !section.id || !existingAlbums.some(a => a.id === section.id);
    const albumId = section.id || slugify(section.displayName) || `section-${index}`;

    // For new albums, create the slot first so that uploadPictures has a target.
    if (isNew) {
      await createPicturesAlbum(albumId, {
        displayName: section.displayName || '',
        order: index,
        pictureOrder: [],
      });
    }

    const existingAlbum = existingAlbums.find(a => a.id === albumId);
    const existingUrls = existingAlbum?.pictureUrls || [];
    const existingPictures = existingUrls.map((url) => {
      const id = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));
      return { id, url };
    });

    const { pictures } = section;
    const picturesToDelete = existingPictures
      .filter((p) => !pictures.some((up) => up.id === p.id))
      .map((p) => p.id);
    const picturesToAdd = pictures.filter((p) => !!p.file);

    try {
      if (picturesToDelete.length) {
        await deletePictures(albumId, picturesToDelete);
      }

      if (picturesToAdd.length) {
        const updatedAlbum = await uploadPictures(albumId, picturesToAdd.map((p) => p.file));

        // Assign real IDs from the API response back to the in-memory picture objects
        // so the pictureOrder computed below uses the correct server-assigned IDs.
        if (updatedAlbum?.pictureUrls) {
          const newUrls = updatedAlbum.pictureUrls;
          for (let i = 0; i < picturesToAdd.length; i++) {
            const newUrl = newUrls[newUrls.length - picturesToAdd.length + i];
            if (newUrl) {
              const newId = newUrl.split('/').pop().split('.').shift();
              const target = pictures.find((p) => p.id === picturesToAdd[i].id);
              if (target) {
                target.id = newId;
                target.url = newUrl;
                target.file = null;
                // Preload so the image is in browser cache before the state reset
                new Image().src = newUrl;
              }
            }
          }
        }
      }

      // Save final metadata with the correct picture order, derived from the
      // pictures array now that all additions and deletions have been applied.
      const pictureOrder = pictures.map(p => p.id).filter(Boolean);
      await updatePicturesAlbum(albumId, {
        displayName: section.displayName || '',
        order: index,
        pictureOrder,
      });
    } catch (error) {
      setAlert({
        message: `Error updating section pictures: ${error.message || 'see server logs for more details'}`,
        severity: 'error'
      });
      throw error;
    }
  }, [
    createPicturesAlbum,
    uploadPictures,
    updatePicturesAlbum,
    deletePictures,
    setAlert,
  ]);

  const onSubmit = useCallback(async (sections) => {
    if (sections.length === 0) {
      setAlert({ message: 'At least one section is required.', severity: 'error' });
      return;
    }

    // The first section without a display name uses the reserved '_main' album ID.
    if (!sections[0].displayName?.trim()) {
      sections[0].id = ALBUM_ID.MAIN;
    }

    const currentAlbums = picturesQueryResults?.data || [];

    // Delete albums that were removed by the user.
    const albumsToDelete = currentAlbums.filter(a => !sections.some(s => s.id === a.id));
    for (const album of albumsToDelete) {
      await deletePicturesAlbum(album.id);
    }

    // Save each section (create/update album + handle picture changes).
    for (let index = 0; index < sections.length; index++) {
      await onSubmitSection(sections[index], index, currentAlbums);
    }
  }, [
    picturesQueryResults,
    deletePicturesAlbum,
    onSubmitSection,
    setAlert,
  ]);

  return (
    <PropertyPicturesEditorContentWithQueryData
      queryData={picturesQueryResults}
      onSubmit={onSubmit}
      deletePicturesAlbum={deletePicturesAlbum}
    />
  );
};

const emptySection = {
  id: null,
  displayName: '',
  order: 0,
  pictureUrls: [],
  pictures: [],
  originalPictureIds: [],
};

const PropertyPicturesEditorContent = ({
  data,
  reload,
  onSubmit,
  deletePicturesAlbum,
}) => {
  const [status, setStatus, statusCodes] = useFormStatus();
  const [valid, setFieldValidity] = useFormValidator();

  const { setAlert } = useAlertNotification();

  // API now returns PicturesAlbum[] directly (no sections wrapper).
  const sectionsFromApi = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const initialSections = useMemo(() => {
    if (sectionsFromApi.length === 0) {
      return [emptySection];
    }
    return sectionsFromApi
      .sort((a, b) => a.order - b.order)
      .map((s) => ({
        id: s.id ?? null,
        displayName: s.name ?? s.displayName ?? '',
        order: s.order,
        pictureUrls: s.pictureUrls || [],
        pictures: pictureUrlsToPictures(s.pictureUrls || []),
        originalPictureIds: (s.pictureUrls || []).map((u) => u.split('/').pop()),
    }));
  }, [sectionsFromApi]);

  const [sections, setSections, isChanged] = useEditableArray(initialSections);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    sections.forEach((section, i) => {
      if (i === 0) {
        setFieldValidity(i, true);
      } else {
        const hasName = (section.displayName || '').trim() !== '';
        const hasPictures = (section.pictures?.length || 0) > 0;
        setFieldValidity(i, hasName && hasPictures);
      }
    });
  }, [sections, setFieldValidity]);

  const handleSectionChange = useCallback((index, field, value) => {
    setSections((prev) => {
      const newSection = { ...prev[index], [field]: value };
      const isValid = (index === 0 || newSection.displayName.trim() !== '') && newSection.pictures.length > 0;
      setFieldValidity(index, isValid);
      const next = [...prev];
      next[index] = newSection;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
  }, [setFieldValidity]);

  const handlePicturesChange = useCallback((index, newPictures) => {
    setSections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], pictures: newPictures };
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
  }, []);

  const handleAddSection = useCallback(() => {
    setSections((prev) => [
      ...prev,
      {
        ...emptySection,
        order: prev.length,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
  }, []);

  const handleRemoveSection = useCallback(
    (index) => {
      if (index === 0) return;
      const section = sections[index];
      const isExisting = section.id !== undefined && section.id !== null;
      if (isExisting) {
        const label = section.id === ALBUM_ID.MAIN ? 'Main gallery' : (section.displayName || section.id);
        setPendingDelete({ id: index, sectionId: section.id, label });
      } else {
        setSections((prev) => prev.filter((_, i) => i !== index));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
    [sections]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    await deletePicturesAlbum(pendingDelete.sectionId);
    setSections((prev) => {
      const next = prev.filter((_, i) => i !== pendingDelete.id);
      return next.length > 0 ? next : [emptySection];
    });
    setPendingDelete(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
  }, [pendingDelete, deletePicturesAlbum]);

  const handleCancelDelete = useCallback(() => setPendingDelete(null), []);

  const handleMoveSection = useCallback((index, direction) => {
    setSections((prev) => {
      const next = [...prev];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, order: i }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable setter from useEditableArray
  }, []);

  const _onSubmit = useCallback(async () => {
    setStatus(statusCodes.SAVING);
    try {
      await onSubmit(sections);
      // Flush in-place URL/ID mutations (set during upload) into React state before
      // React Query invalidation resets useEditableArray. By this point the new MinIO
      // images are already preloading, so the reset causes no visible flash.
      setSections((prev) => prev.map((s) => ({ ...s, pictures: [...(s.pictures || [])] })));
      setAlert({ message: "Successfully updated property pictures", severity: 'success' });
      setStatus(statusCodes.AWAITING_USER_INPUT);
    } catch (error) {
      console.error(error);
      setAlert({
        message: "Failed to update property pictures - see server logs for more details",
        severity: 'error'
      });
      setStatus(statusCodes.AWAITING_USER_INPUT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setStatus is a stable useState setter from useFormStatus
  }, [onSubmit, sections, setAlert, statusCodes]);

  return (
    <Box>
      <Toolbar>
        <Box sx={{ flexGrow: 1 }} />
        <SaveButton
          onClick={_onSubmit}
          disabled={!isChanged || !valid || status !== statusCodes.AWAITING_USER_INPUT}
          loading={status === statusCodes.SAVING}
        />
      </Toolbar>
      <Box sx={{ margin: "0px 20px", pb: 4, overflowAnchor: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Gallery sections</Typography>
          <PlusIcon onClick={handleAddSection} title="Add section" />
        </Box>
        {sections.map((section, index) => (
          <GallerySectionEditor
            key={section.id || `new-${index}`}
            section={section}
            index={index}
            isFirst={index === 0}
            canMoveUp={index > 1}
            canMoveDown={index > 0 && index < sections.length - 1}
            onDisplayNameChange={(value) => handleSectionChange(index, 'displayName', value)}
            onPicturesChange={(newPictures) => handlePicturesChange(index, newPictures)}
            onMoveUp={() => handleMoveSection(index, 'up')}
            onMoveDown={() => handleMoveSection(index, 'down')}
            onRemove={() => handleRemoveSection(index)}
          />
        ))}
        {sections.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No sections yet. Click + to add a section.
          </Typography>
        )}
      </Box>
      <DeleteConfirmationModal
        entity={pendingDelete}
        confirmationMessage={pendingDelete ? `Delete section "${pendingDelete.label}" and all its pictures? This cannot be undone.` : undefined}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

const PropertyPicturesEditorContentWithQueryData = withQueryData(PropertyPicturesEditorContent);

export default PropertyPicturesEditor;
